import { useContext, useEffect, useState } from 'react';
import { ArrowRight, Building2, ChevronRight, MapPin, ShieldCheck, Sparkles, Target, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../config/api';
import { UserAuthContext } from '../../UserAuthContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { productRoute } from '../../utils/namedRoutes';
import { useLocalizedPath } from '../../contexts/LanguageContext';
import './RegionalAdvertising.css';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  appendLocationPreference,
  fetchHomepageJson,
  homepageAuthHeaders,
  homepageViewerScope,
} from '../../utils/homepageApi';

export default function RegionalAdvertising() {
  const { user, loading: authLoading, location, locationReady } = useContext(UserAuthContext);
  const { personalizationAllowed } = useCookieConsent();
  const { formatMoney } = useCurrency();
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const localizedPath = useLocalizedPath();

  useEffect(() => {
    if (authLoading || !locationReady) return undefined;
    const controller = new AbortController();

    const loadAdvertising = async () => {
      setProducts([]);
      try {
        const endpoint = new URL(apiUrl('/api/regional-advertising'));
        endpoint.searchParams.set('personalization', personalizationAllowed ? '1' : '0');
        appendLocationPreference(endpoint, location);
        const result = await fetchHomepageJson(endpoint.toString(), {
          signal: controller.signal,
          headers: homepageAuthHeaders(user),
          scope: homepageViewerScope(user),
        });
        setProducts(Array.isArray(result.products) ? result.products : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading regional advertising:', error);
          setProducts([]);
        }
      }
    };

    loadAdvertising();
    return () => controller.abort();
  }, [authLoading, location, locationReady, personalizationAllowed, user]);

  const openProduct = (product) => navigate(localizedPath(productRoute(product)));
  const advertiseBusiness = () => {
    if (authLoading) return;
    if (Number(user?.account_type) === 1) {
      navigate(localizedPath('/sell/dashboard?tab=advertising'));
      return;
    }
    if (user) {
      navigate(localizedPath('/sell/register'));
      return;
    }
    navigate(localizedPath('/login'), { state: { from: localizedPath('/sell/register') } });
  };

  return (
    <section className="regional-advertising-section" aria-labelledby="regional-advertising-title">
      <div className="regional-advertising-container">
        <div className="regional-advertising-promo">
          <div className="regional-advertising-intro">
            <h2 id="regional-advertising-title">Regional Advertising</h2>
            <p>Promote your seller profile or products to qualified buyers in a selected city, state, or country while organic marketplace relevance remains part of every ranking.</p>
            <button type="button" onClick={advertiseBusiness} disabled={authLoading}>
              Advertise Your Business <ChevronRight size={17} />
            </button>
          </div>
          <ul className="regional-advertising-benefits" aria-label="Regional advertising benefits">
            <li><Target size={19} aria-hidden="true" /><span><strong>Local targeting</strong><small>Choose the city, state, or country where your campaign should compete.</small></span></li>
            <li><TrendingUp size={19} aria-hidden="true" /><span><strong>Priority visibility</strong><small>Eligible paid campaigns receive a controlled boost in matching markets.</small></span></li>
            <li><ShieldCheck size={19} aria-hidden="true" /><span><strong>Transparent promotion</strong><small>Every paid placement is clearly identified as sponsored.</small></span></li>
          </ul>
        </div>

        {products.length > 0 && (
          <div className="regional-advertising-products">
            <header>
              <div>
                <span>Active paid campaigns</span>
                <h3>Sponsored Products for Your Market</h3>
              </div>
              <small>{products.length} promoted product{products.length === 1 ? '' : 's'}</small>
            </header>
            <div className="regional-advertising-grid">
              {products.map((product) => (
                <article
                  className="regional-advertising-card"
                  key={product.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => openProduct(product)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') openProduct(product);
                  }}
                >
                  <div className="regional-advertising-card__image">
                    <img src={product.image} alt={product.name} />
                    <span><Sparkles size={12} /> Sponsored · Paid advertising</span>
                  </div>
                  <div className="regional-advertising-card__content">
                    <small>{product.brand}</small>
                    <h4>{product.name}</h4>
                    <strong>{formatMoney(product.price, product.priceCurrency)}</strong>
                    <div>
                      <span><Building2 size={13} /> {product.seller.name}</span>
                      <span><MapPin size={13} /> {product.seller.location}</span>
                    </div>
                    <button type="button" onClick={(event) => { event.stopPropagation(); openProduct(product); }}>
                      View Product <ArrowRight size={15} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
